import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Paper,
  Stack,
  Divider,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  useMediaQuery,
  useTheme,
  Collapse
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import axiosInstance from '../api/axiosConfig';

interface Member {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  member_number: string;
  member_type: string;
  membership_status: string;
  primary_member_id: string | null;
}

interface CheckInRecord {
  id: string;
  member_id: string | null;
  non_member_dependent_id?: number | null;
  member?: Member;                          // absent for a non-member's child
  non_member_dependent?: { id: number; first_name: string; last_name: string };
  check_in_time: string;
  check_out_time?: string;
  location: string;
  status: string;
}

interface HouseholdMember {
  id: string;
  first_name: string;
  last_name: string;
  member_type: string;
  relationship_to_primary: string | null;
  is_checked_in: boolean;
}

const CheckInManagementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Member[]>([]);
  const [currentlyCheckedIn, setCurrentlyCheckedIn] = useState<CheckInRecord[]>([]);
  const [checkedOutToday, setCheckedOutToday] = useState<CheckInRecord[]>([]);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMember[]>([]);
  const [selectedHousehold, setSelectedHousehold] = useState<string[]>([]);
  const [showHouseholdDialog, setShowHouseholdDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stats, setStats] = useState({
    members_checked_in: 0,
    non_members_checked_in: 0,
    today_members: 0,
    today_non_members: 0,
    today_total: 0
  });
  const [showCheckedIn, setShowCheckedIn] = useState(true);
  const [showCheckedOut, setShowCheckedOut] = useState(false);
  const [checkedInFilter, setCheckedInFilter] = useState('');
  const [checkedOutFilter, setCheckedOutFilter] = useState('');
  // Non-member guest visit dialog state
  const [showGuestVisitDialog, setShowGuestVisitDialog] = useState(false);
  const [pendingNonMemberCheckIn, setPendingNonMemberCheckIn] = useState<any>(null);
  const [countAsGuestVisit, setCountAsGuestVisit] = useState(false);
  // Household guest visit option (for non-member households)
  const [householdCountAsGuestVisit, setHouseholdCountAsGuestVisit] = useState(false);
  const [householdLoading, setHouseholdLoading] = useState(false);

  useEffect(() => {
    fetchCurrentlyCheckedIn();
    fetchCheckedOutToday();
    fetchStats();
  }, []);

  // Debug effect to log householdMembers state changes
  useEffect(() => {
    if (householdMembers.length > 0) {
      console.log('[State Update] householdMembers changed:', householdMembers.map(m => ({ name: m.first_name, is_checked_in: m.is_checked_in })));
      const allCheckedIn = householdMembers.every(m => m.is_checked_in);
      const noneCheckedIn = householdMembers.every(m => !m.is_checked_in);
      console.log('[State Update] Button states - Select All Out disabled:', allCheckedIn, ', Select All In disabled:', noneCheckedIn);
    }
  }, [householdMembers]);

  // Live search as user types (with debounce)
  useEffect(() => {
    const delaySearch = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(delaySearch);
  }, [searchQuery]);

  const fetchCurrentlyCheckedIn = async () => {
    try {
      const response = await axiosInstance.get('/check-ins/currently-checked-in');
      const checkedInList = response.data.data || [];
      setCurrentlyCheckedIn(checkedInList);
      return checkedInList; // Return the data for use in fetchStats
    } catch (error) {
      console.error('Failed to fetch currently checked-in members:', error);
      return [];
    }
  };

  const fetchCheckedOutToday = async () => {
    try {
      // Get local date (not UTC) to avoid timezone issues
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;

      const response = await axiosInstance.get('/check-ins/daily', {
        params: { date: today }
      });

      const allToday = response.data.data || [];
      // Filter to only show checked-out members (status = 'checked_out')
      const checkedOut = allToday.filter((record: CheckInRecord) => record.status === 'checked_out');
      setCheckedOutToday(checkedOut);
    } catch (error) {
      console.error('Failed to fetch checked-out members:', error);
    }
  };

  // Helper to check if a record is for a non-member (incl. a non-member's child)
  const isNonMemberRecord = (record: CheckInRecord) => {
    if (record.non_member_dependent) return true;
    const status = record.member?.membership_status;
    return status?.toLowerCase().startsWith('non_member') ?? false;
  };

  // A check-in record's display name + member number — works for members AND a
  // non-member's child (which has no `member` object).
  const recordName = (r: CheckInRecord) => {
    const p = r.member ?? r.non_member_dependent;
    return p ? `${p.first_name} ${p.last_name}` : 'Guest';
  };
  const recordNumber = (r: CheckInRecord) => r.member?.member_number ?? '';

  const fetchStats = async (currentCheckedIn?: CheckInRecord[]) => {
    try {
      // Get local date (not UTC) to avoid timezone issues
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const today = `${year}-${month}-${day}`;

      const response = await axiosInstance.get('/check-ins/daily', { params: { date: today } });

      // Use passed-in data or fetch fresh data
      const checkedInData = currentCheckedIn !== undefined ? currentCheckedIn : await fetchCurrentlyCheckedIn();
      const todayData = response.data.data || [];

      // Calculate separate stats for members vs non-members
      const membersCheckedIn = checkedInData.filter((r: CheckInRecord) => !isNonMemberRecord(r));
      const nonMembersCheckedIn = checkedInData.filter((r: CheckInRecord) => isNonMemberRecord(r));
      const todayMembers = todayData.filter((r: CheckInRecord) => !isNonMemberRecord(r));
      const todayNonMembers = todayData.filter((r: CheckInRecord) => isNonMemberRecord(r));

      setStats({
        members_checked_in: membersCheckedIn.length,
        non_members_checked_in: nonMembersCheckedIn.length,
        today_members: todayMembers.length,
        today_non_members: todayNonMembers.length,
        today_total: todayData.length
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // Search both members and non-members in parallel
      const [membersResponse, nonMembersResponse] = await Promise.all([
        axiosInstance.get('/members/', { params: { query: searchQuery } }),
        axiosInstance.get('/non-members/search', { params: { q: searchQuery } })
      ]);

      const members = membersResponse.data.data || [];
      const nonMembers = (nonMembersResponse.data.data || []).map((nm: any) => ({
        ...nm,
        is_non_member: true // Flag to identify non-members in the UI
      }));

      // Combine results - members first, then non-members
      const combinedResults = [...members, ...nonMembers];
      setSearchResults(combinedResults);

      // If household members are included (member number search), auto-select and display them
      if (membersResponse.data.household_members && members.length === 1) {
        const member = members[0];
        setSelectedMember(member);

        // Clear old data and show loading
        setHouseholdMembers([]);
        setSelectedHousehold([]);
        setHouseholdLoading(true);
        setShowHouseholdDialog(true);

        // Clear search results to show only the household view
        setSearchResults([]);

        // Map household members with check-in status
        try {
          console.log('[Search auto-open] Fetching status for household members...');
          const membersWithStatus = await Promise.all(
            membersResponse.data.household_members.map(async (m: any) => {
              try {
                // Add cache-busting timestamp to ensure fresh data
                const statusResponse = await axiosInstance.get(`/check-ins/member/${m.id}/status`, {
                  params: { _t: Date.now() }
                });
                console.log(`[Search auto-open] Status for ${m.first_name}: is_checked_in =`, statusResponse.data.is_checked_in, 'raw:', statusResponse.data);
                return {
                  ...m,
                  is_checked_in: statusResponse.data.is_checked_in === true,
                  check_in_time: statusResponse.data.data?.check_in_time
                };
              } catch (err) {
                console.error(`[Search auto-open] Failed to get status for ${m.id}:`, err);
                return { ...m, is_checked_in: false };
              }
            })
          );
          console.log('[Search auto-open] Final membersWithStatus:', membersWithStatus.map(m => ({ name: m.first_name, is_checked_in: m.is_checked_in })));
          setHouseholdMembers(membersWithStatus);
        } finally {
          setHouseholdLoading(false);
        }
      }
    } catch (error) {
      console.error('Search failed:', error);
      setError('Failed to search members');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleMemberSelect = async (member: Member, openDialog: boolean = false) => {
    setSelectedMember(member);
    setError(null);
    setSuccess(null);

    // Clear existing household data and show loading
    if (openDialog) {
      setHouseholdMembers([]);
      setSelectedHousehold([]);
      setHouseholdLoading(true);
      setShowHouseholdDialog(true); // Open dialog immediately with loading state
    }

    // Fetch household members if PRIMARY or SECONDARY
    if (member.member_type === 'PRIMARY' || member.member_type === 'SECONDARY') {
      try {
        // Use the membership hierarchy endpoint to get full household details
        const householdResponse = await axiosInstance.get(`/membership-hierarchy/household/${member.id}`);

        if (householdResponse.data.members) {
          // Map the members to include check-in status
          const membersWithStatus = await Promise.all(
            householdResponse.data.members.map(async (m: any) => {
              try {
                // Add cache-busting timestamp to ensure fresh data
                const statusResponse = await axiosInstance.get(`/check-ins/member/${m.id}/status`, {
                  params: { _t: Date.now() }
                });
                console.log(`[handleMemberSelect] Status for ${m.first_name}: is_checked_in =`, statusResponse.data.is_checked_in, 'raw response:', statusResponse.data);
                return {
                  ...m,
                  is_checked_in: statusResponse.data.is_checked_in === true,
                  check_in_time: statusResponse.data.data?.check_in_time
                };
              } catch (err) {
                console.error(`[handleMemberSelect] Failed to get status for ${m.id}:`, err);
                return { ...m, is_checked_in: false };
              }
            })
          );
          console.log('[handleMemberSelect] Final membersWithStatus:', membersWithStatus.map(m => ({ name: m.first_name, is_checked_in: m.is_checked_in })));
          setHouseholdMembers(membersWithStatus);
        }
      } catch (error) {
        console.error('Failed to fetch household:', error);
        setError('Failed to load household members');
      } finally {
        if (openDialog) {
          setHouseholdLoading(false);
        }
      }
    } else {
      if (openDialog) {
        setHouseholdLoading(false);
      }
    }
  };

  const handleQuickCheckIn = async (member: any) => {
    // For non-members, show dialog to ask about guest visit
    if (member.is_non_member) {
      setPendingNonMemberCheckIn(member);
      setCountAsGuestVisit(false); // Default to not counting
      setShowGuestVisitDialog(true);
      return;
    }

    // For regular members, check in directly
    await performCheckIn(member.id, false);
  };

  const performCheckIn = async (memberId: string, countAsGuestVisit: boolean) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post(`/check-ins/staff/check-in/${memberId}`, {
        location: 'Main Club',
        count_as_guest_visit: countAsGuestVisit
      });
      setSuccess(response.data.message || 'Checked in successfully');
      const updatedList = await fetchCurrentlyCheckedIn();
      await fetchCheckedOutToday();
      await fetchStats(updatedList);
      setSearchResults([]);
      setSearchQuery('');
      setSelectedMember(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check in');
    } finally {
      setLoading(false);
    }
  };

  const handleGuestVisitDialogConfirm = async () => {
    if (pendingNonMemberCheckIn) {
      setShowGuestVisitDialog(false);
      await performCheckIn(pendingNonMemberCheckIn.id, countAsGuestVisit);
      setPendingNonMemberCheckIn(null);
    }
  };

  const handleGuestVisitDialogCancel = () => {
    setShowGuestVisitDialog(false);
    setPendingNonMemberCheckIn(null);
    setCountAsGuestVisit(false);
  };

  const handleQuickCheckOut = async (memberId: string | null, nonMemberDependentId?: number | null) => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const body: Record<string, unknown> = { notes: 'Staff checkout from Front Desk' };
      if (nonMemberDependentId != null) body.non_member_dependent_id = nonMemberDependentId;
      // member_id is in the URL; for a non-member's child it's a placeholder and the body carries the real id.
      await axiosInstance.post(`/check-ins/staff/check-out/${memberId ?? '0'}`, body);
      setSuccess('Member checked out successfully');
      const updatedList = await fetchCurrentlyCheckedIn();
      await fetchCheckedOutToday();
      await fetchStats(updatedList);
      setSelectedMember(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check out member');
    } finally {
      setLoading(false);
    }
  };

  // Helper to check if a household member is a non-member
  const isNonMemberHouseholdMember = (member: HouseholdMember) => {
    const status = (member as any).membership_status;
    // membership_status can be lowercase (from enum .value) like "non_member_active"
    return status?.toLowerCase().startsWith('non_member');
  };

  // Check if any selected household members are non-members
  const hasSelectedNonMembers = () => {
    return selectedHousehold.some(memberId => {
      const member = householdMembers.find(m => m.id === memberId);
      return member && isNonMemberHouseholdMember(member);
    });
  };

  const handleHouseholdCheckIn = async () => {
    // Filter to only members who are NOT already checked in
    const toCheckIn = selectedHousehold.filter(id => {
      const member = householdMembers.find(m => m.id === id);
      return member && !member.is_checked_in;
    });

    if (toCheckIn.length === 0) {
      setError('No members selected for check-in (selected members may already be checked in)');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      let successCount = 0;
      const errors: string[] = [];

      for (const memberId of toCheckIn) {
        try {
          const member = householdMembers.find(m => m.id === memberId);
          const isNonMember = member && isNonMemberHouseholdMember(member);

          await axiosInstance.post(`/check-ins/staff/check-in/${memberId}`, {
            location: 'Main Club',
            count_as_guest_visit: isNonMember ? householdCountAsGuestVisit : false
          });
          successCount++;
        } catch (err: any) {
          errors.push(err.response?.data?.error || `Failed to check in member ${memberId}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Checked in ${successCount} household member(s)${errors.length > 0 ? ` (${errors.length} failed)` : ''}`);
      }
      if (errors.length > 0 && successCount === 0) {
        setError(errors.join(', '));
      }

      setShowHouseholdDialog(false);
      setSelectedHousehold([]);
      setHouseholdCountAsGuestVisit(false);
      const updatedList = await fetchCurrentlyCheckedIn();
      await fetchCheckedOutToday();
      await fetchStats(updatedList);
      setSearchResults([]);
      setSearchQuery('');
      setSelectedMember(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check in household members');
    } finally {
      setLoading(false);
    }
  };

  const handleHouseholdCheckOut = async () => {
    // Filter to only members who ARE checked in
    const toCheckOut = selectedHousehold.filter(id => {
      const member = householdMembers.find(m => m.id === id);
      return member && member.is_checked_in;
    });

    if (toCheckOut.length === 0) {
      setError('No members selected for check-out (selected members may not be checked in)');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      let successCount = 0;
      const errors: string[] = [];

      for (const memberId of toCheckOut) {
        try {
          await axiosInstance.post(`/check-ins/staff/check-out/${memberId}`, {
            notes: 'Staff checkout from Front Desk - Household'
          });
          successCount++;
        } catch (err: any) {
          errors.push(err.response?.data?.error || `Failed to check out member ${memberId}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`Checked out ${successCount} household member(s)${errors.length > 0 ? ` (${errors.length} failed)` : ''}`);
      }
      if (errors.length > 0 && successCount === 0) {
        setError(errors.join(', '));
      }

      setShowHouseholdDialog(false);
      setSelectedHousehold([]);
      setHouseholdCountAsGuestVisit(false);
      const updatedList = await fetchCurrentlyCheckedIn();
      await fetchCheckedOutToday();
      await fetchStats(updatedList);
      setSearchResults([]);
      setSearchQuery('');
      setSelectedMember(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check out household members');
    } finally {
      setLoading(false);
    }
  };

  const isMemberCheckedIn = (memberId: string) => {
    return currentlyCheckedIn.some(record => record.member_id === memberId);
  };

  const handleCheckOutAll = async () => {
    if (currentlyCheckedIn.length === 0) {
      setError('No members currently checked in');
      return;
    }

    if (!window.confirm(`Are you sure you want to check out all ${currentlyCheckedIn.length} member(s)?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axiosInstance.post('/check-ins/checkout-all', {
        notes: 'End of day checkout'
      });
      setSuccess(response.data.message || 'All members checked out successfully');
      const updatedList = await fetchCurrentlyCheckedIn();
      await fetchCheckedOutToday();
      await fetchStats(updatedList);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to check out all members');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          mb: 2,
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
              Front Desk Check-In
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Manage member check-ins and check-outs
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            size="large"
            startIcon={<LogoutIcon />}
            onClick={handleCheckOutAll}
            disabled={loading || currentlyCheckedIn.length === 0}
            sx={{ minWidth: { sm: 200 }, width: { xs: '100%', sm: 'auto' } }}
          >
            Check Out All ({currentlyCheckedIn.length})
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* 1. Member Search - Full Width */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' } }}>
              Member Search
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: searchResults.length > 0 ? 2 : 0 }}>
              <TextField
                fullWidth
                placeholder="Search members or guests by name, email, or number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={searchLoading}
                sx={{ minWidth: { xs: '100%', sm: 100 } }}
              >
                {searchLoading ? <CircularProgress size={24} /> : 'Search'}
              </Button>
            </Stack>

            {searchResults.length > 0 && (
              <List>
                {searchResults.map((member) => {
                  const isCheckedIn = isMemberCheckedIn(member.id);
                  return (
                    <ListItem
                      key={member.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: isCheckedIn ? 'success.50' : 'background.paper',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 1, sm: 0 },
                        py: { xs: 1.5, sm: 1 }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar sx={{ mr: 2, bgcolor: isCheckedIn ? 'success.main' : 'primary.main', display: { xs: 'none', sm: 'flex' } }}>
                          <PersonIcon />
                        </Avatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                                {member.first_name} {member.last_name}
                              </Typography>
                              {(member as any).is_non_member && (
                                <Chip
                                  label={`Guest ${(member as any).guest_visits_this_year || 0}/${(member as any).guest_annual_limit || 6}`}
                                  size="small"
                                  color="warning"
                                />
                              )}
                              {isCheckedIn && (
                                <Chip
                                  label="Checked In"
                                  size="small"
                                  color="success"
                                  icon={<CheckCircleIcon />}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                              {member.email} • #{member.member_number}
                              <br />
                              {(member as any).is_non_member ? 'Non-Member' : member.member_type} • {member.membership_status?.replace('_', ' ')}
                            </Box>
                          }
                        />
                      </Box>
                      <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        spacing={1}
                        sx={{
                          width: { xs: '100%', sm: 'auto' },
                          justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                          flexShrink: 0,
                          mt: { xs: 1, sm: 0 }
                        }}
                      >
                        {(member.member_type === 'PRIMARY' || member.member_type === 'SECONDARY') && (
                          <IconButton
                            color="primary"
                            onClick={() => handleMemberSelect(member, true)}
                            disabled={loading}
                            size="small"
                          >
                            <PeopleIcon />
                          </IconButton>
                        )}
                        {isCheckedIn ? (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            startIcon={<LogoutIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />}
                            onClick={() => handleQuickCheckOut(member.id)}
                            disabled={loading}
                            sx={{ flex: { xs: 1, sm: 'none' } }}
                          >
                            Check Out
                          </Button>
                        ) : (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<LoginIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />}
                            onClick={() => handleQuickCheckIn(member)}
                            disabled={loading}
                            sx={{ flex: { xs: 1, sm: 'none' } }}
                          >
                            Check In
                          </Button>
                        )}
                      </Stack>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </CardContent>
        </Card>

        {/* 2. Stats Row */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          {/* Member Stats */}
          <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: 'primary.50' }}>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {stats.members_checked_in}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Members Checked-In
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {stats.today_members}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Today Members
            </Typography>
          </Paper>
          {/* Non-Member Stats */}
          <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: 'warning.50' }}>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {stats.non_members_checked_in}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Non-Members Checked-In
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, textAlign: 'center', flex: 1 }}>
            <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
              {stats.today_non_members}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Today Non-Members
            </Typography>
          </Paper>
          {/* Total */}
          <Paper sx={{ p: 2, textAlign: 'center', flex: 1, bgcolor: 'grey.100' }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {stats.today_total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Today
            </Typography>
          </Paper>
        </Stack>

        {/* 3. Currently Checked In - Collapsible */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 0 },
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 1,
                p: 1,
                mx: -1
              }}
              onClick={() => setShowCheckedIn(!showCheckedIn)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                  {showCheckedIn ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, whiteSpace: 'nowrap' }}
                >
                  Currently Checked In
                </Typography>
                <Chip
                  label={currentlyCheckedIn.length}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Filter..."
                  value={checkedInFilter}
                  onChange={(e) => setCheckedInFilter(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: { xs: 1, sm: 'initial' }, width: { sm: 150 } }}
                />
                <IconButton
                  onClick={(e) => { e.stopPropagation(); setCheckedInFilter(''); fetchCurrentlyCheckedIn(); }}
                  size="small"
                  title="Refresh"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Collapse in={showCheckedIn}>
              <Box>
                <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
                {currentlyCheckedIn.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No members currently checked in
                  </Typography>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5
                  }}>
                    {currentlyCheckedIn
                      .filter((record) => {
                        if (!checkedInFilter.trim()) return true;
                        const searchTerm = checkedInFilter.toLowerCase();
                        const fullName = recordName(record).toLowerCase();
                        const memberNumber = recordNumber(record).toLowerCase();
                        return fullName.includes(searchTerm) || memberNumber.includes(searchTerm);
                      })
                      .map((record) => (
                      <Paper
                        key={record.id}
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          bgcolor: isNonMemberRecord(record) ? 'warning.50' : 'background.paper',
                          borderColor: isNonMemberRecord(record) ? 'warning.main' : 'divider'
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                            {recordName(record)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {new Date(record.check_in_time).toLocaleTimeString('en-US', {
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true
                            })}
                            {isNonMemberRecord(record) && ' • Guest'}
                          </Typography>
                        </Box>
                        <IconButton
                          onClick={() => handleQuickCheckOut(record.member_id, record.non_member_dependent_id)}
                          disabled={loading}
                          color="error"
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          <LogoutIcon fontSize="small" />
                        </IconButton>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* 4. Checked Out Today Section - Collapsible */}
        <Card>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: { xs: 1, sm: 0 },
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
                borderRadius: 1,
                p: 1,
                mx: -1
              }}
              onClick={() => setShowCheckedOut(!showCheckedOut)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                  {showCheckedOut ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{ fontSize: { xs: '1.125rem', md: '1.25rem' }, whiteSpace: 'nowrap' }}
                >
                  Checked Out Today
                </Typography>
                <Chip
                  label={checkedOutToday.length}
                  size="small"
                  color="default"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Filter..."
                  value={checkedOutFilter}
                  onChange={(e) => setCheckedOutFilter(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: { xs: 1, sm: 'initial' }, width: { sm: 150 } }}
                />
                <IconButton
                  onClick={(e) => { e.stopPropagation(); setCheckedOutFilter(''); fetchCheckedOutToday(); }}
                  size="small"
                  title="Refresh"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
            <Collapse in={showCheckedOut}>
              <Box>
                <Divider sx={{ my: { xs: 1.5, md: 2 } }} />
                {checkedOutToday.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No members checked out today
                  </Typography>
                ) : (
                  <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                    gap: 1.5
                  }}>
                    {checkedOutToday
                      .filter((record) => {
                        if (!checkedOutFilter.trim()) return true;
                        const searchTerm = checkedOutFilter.toLowerCase();
                        const fullName = recordName(record).toLowerCase();
                        const memberNumber = recordNumber(record).toLowerCase();
                        return fullName.includes(searchTerm) || memberNumber.includes(searchTerm);
                      })
                      .map((record) => (
                      <Paper
                        key={record.id}
                        variant="outlined"
                        sx={{ p: 1.5, bgcolor: 'grey.50' }}
                      >
                        <Typography variant="subtitle2" noWrap sx={{ fontSize: { xs: '0.875rem', sm: '0.9rem' } }}>
                          {recordName(record)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          In: {new Date(record.check_in_time).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                          {' • '}
                          Out: {record.check_out_time ? new Date(record.check_out_time).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          }) : 'N/A'}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </CardContent>
        </Card>

        {/* Household Check-In/Out Dialog */}
        <Dialog
          open={showHouseholdDialog}
          onClose={() => setShowHouseholdDialog(false)}
          maxWidth="sm"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                Household Check-In / Out
              </Typography>
              <Box>
                <IconButton
                  onClick={() => selectedMember && handleMemberSelect(selectedMember, true)}
                  size="small"
                  disabled={householdLoading}
                  title="Refresh status"
                >
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={() => setShowHouseholdDialog(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ pt: { xs: 1, sm: 2 } }}>
            {householdLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : householdMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                No household members found
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                  Select household members:
                </Typography>

                {/* Quick select buttons */}
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const notCheckedIn = householdMembers.filter(m => !m.is_checked_in).map(m => m.id);
                      setSelectedHousehold(notCheckedIn);
                    }}
                    disabled={householdMembers.every(m => m.is_checked_in)}
                  >
                    Check All In
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const checkedIn = householdMembers.filter(m => m.is_checked_in).map(m => m.id);
                      setSelectedHousehold(checkedIn);
                    }}
                    disabled={householdMembers.every(m => !m.is_checked_in)}
                  >
                    Check All Out
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setSelectedHousehold([])}
                    disabled={selectedHousehold.length === 0}
                  >
                    Clear
                  </Button>
                </Stack>

                <FormGroup>
                  {householdMembers.map((member) => (
                    <FormControlLabel
                      key={member.id}
                      sx={{
                        mb: { xs: 1, sm: 0.5 },
                        border: 1,
                        borderColor: member.is_checked_in ? 'success.main' : 'divider',
                        bgcolor: member.is_checked_in ? 'success.50' : 'background.paper',
                        borderRadius: 1,
                        p: { xs: 1, sm: 1 },
                        ml: 0,
                        mr: 0,
                        width: '100%'
                      }}
                      control={
                        <Checkbox
                          checked={selectedHousehold.includes(member.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedHousehold([...selectedHousehold, member.id]);
                            } else {
                              setSelectedHousehold(selectedHousehold.filter(id => id !== member.id));
                            }
                          }}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: '100%', ml: 1 }}>
                          <Typography sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                            {member.first_name} {member.last_name}
                          </Typography>
                          {member.relationship_to_primary && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' } }}>
                              ({member.relationship_to_primary})
                            </Typography>
                          )}
                          {isNonMemberHouseholdMember(member) && (
                            <Chip
                              label={`Guest ${(member as any).guest_visits_this_year || 0}/${(member as any).guest_annual_limit || 6}`}
                              size="small"
                              color="warning"
                              sx={{ height: { xs: 20, sm: 24 } }}
                            />
                          )}
                          {member.is_checked_in && (
                            <Chip
                              label="Checked In"
                              size="small"
                              color="success"
                              icon={<CheckCircleIcon />}
                              sx={{ height: { xs: 20, sm: 24 } }}
                            />
                          )}
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>

                {/* Guest visit option for non-member households - only show for check-ins */}
                {hasSelectedNonMembers() && selectedHousehold.some(id => {
                  const member = householdMembers.find(m => m.id === id);
                  return member && !member.is_checked_in;
                }) && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={householdCountAsGuestVisit}
                          onChange={(e) => setHouseholdCountAsGuestVisit(e.target.checked)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2">Count as guest visit for non-members</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Check this for regular club visits. Leave unchecked for classes/lessons.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: 'space-between' }}>
            <Button
              onClick={() => {
                setShowHouseholdDialog(false);
                setSelectedHousehold([]);
                setHouseholdCountAsGuestVisit(false);
              }}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Cancel
            </Button>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                onClick={handleHouseholdCheckIn}
                disabled={loading || !selectedHousehold.some(id => !householdMembers.find(m => m.id === id)?.is_checked_in)}
                startIcon={loading ? <CircularProgress size={20} /> : <LoginIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Check In Selected
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleHouseholdCheckOut}
                disabled={loading || !selectedHousehold.some(id => householdMembers.find(m => m.id === id)?.is_checked_in)}
                startIcon={loading ? <CircularProgress size={20} /> : <LogoutIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Check Out Selected
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        {/* Guest Visit Confirmation Dialog for Non-Members */}
        <Dialog
          open={showGuestVisitDialog}
          onClose={handleGuestVisitDialogCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            Check In Non-Member
          </DialogTitle>
          <DialogContent>
            {pendingNonMemberCheckIn && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>{pendingNonMemberCheckIn.first_name} {pendingNonMemberCheckIn.last_name}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current guest visits: {pendingNonMemberCheckIn.guest_visits_this_year || 0} / {pendingNonMemberCheckIn.guest_annual_limit || 6}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={countAsGuestVisit}
                      onChange={(e) => setCountAsGuestVisit(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">Count as guest visit</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Check this for regular club visits. Leave unchecked for classes/lessons.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleGuestVisitDialogCancel}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleGuestVisitDialogConfirm}
              disabled={loading}
              startIcon={<LoginIcon />}
            >
              Check In
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default CheckInManagementPage;
